import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PhoneMockup } from "@/components/PhoneMockup";
import { Reveal, WordReveal, Counter, RotatingText } from "@/components/anim";
import { trackWhop } from "@/lib/whop";
import { clarityEvent } from "@/lib/clarity";

const TOOLS = ["TikTok", "Instagram", "Canva", "Stripe", "Shopify", "WhatsApp", "ChatGPT", "Google Maps"];

const HERO_ROTATE = [
  "lavage auto à domicile",
  "montage vidéo pour coiffeurs",
  "coach sportif en extérieur",
  "photographe pour Airbnb",
  "menus pour restaurants",
  "templates Notion pour étudiants",
  "revente de sneakers",
  "meal prep pour sportifs",
];

const STEPS = [
  { n: "01", title: "Réponds", text: "Ton âge, ton temps, tes rêves, ce qui te bloque. On veut ta vraie situation." },
  { n: "02", title: "Découvre ton business", text: "Un business en ligne ou physique, choisi pour toi et pas pour tout le monde." },
  { n: "03", title: "Suis ton plan", text: "30 jours d'actions concrètes, une par une, jusqu'à tes premiers euros." },
];

const METRICS = [
  { n: 21, label: "questions sur ta situation réelle" },
  { n: 1, label: "business taillé pour toi" },
  { n: 30, label: "jours de plan, jour par jour" },
];

const FAQ = [
  { q: "Je n'ai aucune idée de business, c'est pour moi ?", a: "C'est exactement pour toi. Tu n'as rien à apporter à part tes réponses : l'idée, c'est notre travail." },
  { q: "Je n'ai pas d'argent à investir, ça marche quand même ?", a: "Oui. Ton budget fait partie de l'analyse : si tu pars de zéro, ton business sera lançable avec zéro." },
  { q: "En ligne ou physique, comment vous choisissez ?", a: "C'est toi qui nous dis ce que tu préfères. Si tu hésites, l'IA choisit selon ton profil et t'explique pourquoi." },
  { q: "Et si le business proposé ne me plaît pas ?", a: "Ton résultat repose sur tes réponses : plus elles sont honnêtes, plus il te ressemble. Si le business ne te convient pas, tu peux en générer un nouveau." },
  { q: "Comment fonctionne l'abonnement ?", a: "Tu choisis une formule mensuelle. Elle se renouvelle automatiquement et tu la résilies en un clic depuis ton compte, à tout moment." },
  { q: "Je peux retrouver mon résultat plus tard ?", a: "Oui, avec ton compte tu retrouves ton business et ton plan à tout moment, sur n'importe quel appareil." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [showSticky, setShowSticky] = useState(false);
  const heroCtaRef = useRef(null);

  useEffect(() => { trackWhop("view_content"); }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!heroCtaRef.current) return;
      setShowSticky(heroCtaRef.current.getBoundingClientRect().bottom < 0);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const start = () => { trackWhop("contact"); clarityEvent("clic_cta_accueil"); navigate("/quiz"); };

  return (
    <div>
      <Navbar />
      <main>
        <section className="container hero" data-testid="landing-hero">
          <div className="hero-grid">
            <div className="hero-copy">
              <Reveal><p className="eyebrow">IDEVORAI — DES IDÉES QUI TE DONNENT FAIM DE RÉUSSIR.</p></Reveal>
              <h1 data-testid="hero-title">
                <WordReveal text="Trouve ton idée de business et crée déjà des revenus avec cette idée." stagger={0.08} highlight={["revenus"]} />
              </h1>
              <div className="hero-rotator" data-testid="hero-rotator">
                Ton business pourrait être : <RotatingText items={HERO_ROTATE} className="rot" />
              </div>
              <Reveal delay={0.5}>
                <p className="hero-description">L'IA crée ton business sur-mesure et ton plan de 30 jours pour encaisser tes premiers euros.</p>
              </Reveal>
              <Reveal delay={0.6}>
                <div className="hero-flow mono"><span><b>01</b> Tu réponds</span><span>—</span><span><b>02</b> L'IA analyse</span><span>—</span><span><b>03</b> Tu encaisses</span></div>
              </Reveal>
              <Reveal delay={0.7}>
                <div ref={heroCtaRef} className="hero-actions">
                  <button onClick={start} data-testid="hero-cta" className="btn-primary btn-reveal">Révèle mon business <span aria-hidden="true">→</span></button>
                </div>
              </Reveal>
              <Reveal delay={0.8}>
                <div className="social-proof" data-testid="hero-social-proof"><strong>+50 000</strong><span>utilisateurs</span></div>
              </Reveal>
            </div>
            <Reveal className="hero-visual" delay={0.2}><PhoneMockup /></Reveal>
          </div>
        </section>

        <section className="tools-strip" aria-label="Les outils pour ton business">
          <p className="container eyebrow tools-label">Les outils avec lesquels tu vas lancer ton business</p>
          <div className="tools-track">
            {[0, 1].map((copy) => <div className="tools-set" key={copy} aria-hidden={copy === 1}>{TOOLS.map((tool) => <span className="tool-name" key={tool}>{tool}</span>)}</div>)}
          </div>
        </section>

        <section className="container metrics metrics-3" aria-label="idevorai en chiffres">
          {METRICS.map((c, i) => (
            <div className="metric" key={c.n} data-testid={`landing-stat-${i}`}>
              <div className="metric-number"><Counter to={c.n} /></div>
              <p className="metric-label">{c.label}</p>
            </div>
          ))}
        </section>

        <section id="comment" className="container section">
          <div className="section-heading">
            <Reveal><p className="eyebrow">01 — COMMENT ÇA MARCHE</p></Reveal>
            <h2><WordReveal text="Comment ça marche." stagger={0.06} highlight={["marche"]} /></h2>
          </div>
          <div className="steps">
            {STEPS.map((s, i) => <Reveal className="step" key={s.n} delay={i * 0.1}><span className="step-number mono">{s.n} /</span><h3>{s.title}</h3><p>{s.text}</p></Reveal>)}
          </div>
        </section>

        <section id="questions" className="faq-section section">
          <div className="container faq-layout">
            <div>
              <Reveal><p className="eyebrow">02 — QUESTIONS FRÉQUENTES</p></Reveal>
              <h2><WordReveal text="Les questions qu'on nous pose souvent." stagger={0.05} highlight={["pose", "souvent"]} /></h2>
            </div>
            <Reveal delay={0.15}>
              <Accordion type="single" collapsible>
                {FAQ.map((f, i) => <AccordionItem key={i} value={`item-${i}`} className="faq-item" data-testid={`faq-${i}`}><AccordionTrigger className="faq-trigger" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger><AccordionContent className="faq-answer" data-testid={`faq-answer-${i}`}>{f.a}</AccordionContent></AccordionItem>)}
              </Accordion>
            </Reveal>
          </div>
        </section>

        <section className="container section">
          <Reveal className="final-cta">
            <div><p className="eyebrow">03 — À TOI DE JOUER</p><h2>Ton business t'attend.<br />Il ne manque plus que <em>toi.</em></h2></div>
            <div><button onClick={start} data-testid="final-cta" className="btn-primary btn-reveal">Révèle mon business <span aria-hidden="true">→</span></button></div>
          </Reveal>
        </section>
      </main>
      <Footer />
      {showSticky && <div className="mobile-sticky md:hidden"><button onClick={start} data-testid="sticky-cta" className="btn-primary btn-reveal w-full">Révèle mon business <span aria-hidden="true">→</span></button></div>}
    </div>
  );
}
